import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { UserInfoComponent } from '../../components/user-info/user-info.component';
import { UpdateUserComponent } from '../../components/update-user/update-user.component';
import { UserHistoryComponent } from '../../components/user-history/user-history.component';
import { SharedMaterialUIModule } from '@shared/modules/shared-material-ui.module';
import { PermissionService } from '@core/services/permission.service';
import { Permission } from '@shared/enums/permission.enum';
import {
  UpdateUserProfileRequest,
  UpdateUserRequest,
  UserResponse,
} from '../../user.model';
import { UserStore } from '../../user.store';
import { ClientStore } from '@features/client/client.store';
import { AffiliateStore } from '@features/affiliate/affiliate.store';
import { LookupStore } from '@core/lookup/lookup.store';
import { SearchableLookupOption } from '@shared/components/searchable-lookup-select/searchable-lookup-select.component';
import {SHARED_IMPORTS} from "../../../../shared/modules/shared-imports.module";
import { DetailsPageLayoutComponent } from '@core/layout/page-layouts/details-page-layout/details-page-layout-component';

@Component({
  selector: 'app-user-management-detail-page',
  imports: [
    RouterLink,
    UserInfoComponent,
    UpdateUserComponent,
    UserHistoryComponent,
    ...SharedMaterialUIModule,
    ...SHARED_IMPORTS,
    DetailsPageLayoutComponent
  ],
  templateUrl: './user-management-detail.page.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class UserManagementDetailPage {
  private readonly route = inject(ActivatedRoute);
  private readonly permissionService = inject(PermissionService);

  protected readonly userStore = inject(UserStore);
  protected readonly clientStore = inject(ClientStore);
  protected readonly affiliateStore = inject(AffiliateStore);
  protected readonly lookupStore = inject(LookupStore);

  protected readonly affiliateOptions = computed<SearchableLookupOption[]>(() => {
    const options = this.affiliateStore.affiliateLookupResponses().map((item) => ({
      id: item.id,
      name: `${item.name} ${item.surname}`.trim(),
      code: null,
    }));

    // Ensure the user's currently-linked affiliate always has a matching option, even when it
    // falls outside the current search/page results from the lookup endpoint.
    const current = this.userStore.selectedUser();
    if (current?.affiliateId && !options.some((option) => option.id === current.affiliateId)) {
      options.push({ id: current.affiliateId, name: current.affiliate, code: null });
    }

    return options;
  });

  constructor() {
    this.clientStore.loadClients({ includeInactive: false });
    this.affiliateStore.loadAffiliateLookupResponses();
    this.lookupStore.loadUserManagementLookups();
    this.loadUser();
  }

  protected affiliateSearchChanged(search: string): void {
    this.affiliateStore.loadAffiliateLookupResponses(search);
  }

  protected canUpdate(): boolean {
    return this.permissionService.hasPermission(
      Permission.UsersManagementUpdate
    );
  }

  protected canDeactivate(): boolean {
    return this.permissionService.hasPermission(
      Permission.UsersManagementDelete
    );
  }

  protected canActivate(): boolean {
    return this.permissionService.hasPermission(
      Permission.UsersManagementUpdate
    );
  }

  protected loadUser(): void {
    const userId = this.route.snapshot.paramMap.get('userId');

    if (!userId) {
      return;
    }

    this.userStore.loadUser(userId);
    this.userStore.loadUserHistory(userId);
  }

  protected saveUser(
    update: UpdateUserRequest | UpdateUserProfileRequest
  ): void {
    const userId = this.userStore.selectedUser()?.id;

    if (!userId || !('portalId' in update)) {
      return;
    }

    this.userStore.updateUser(userId, update);
  }

  protected toggleUserActive(user: UserResponse): void {
    if (user.isActive) {
      if (!this.canDeactivate()) {
        return;
      }

      this.userStore.deactivateUser(user.id);
      return;
    }

    if (!this.canActivate()) {
      return;
    }

    this.userStore.activateUser(user.id);
  }
}
